import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, FileCog, Loader2, Plus, RotateCcw } from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import type { InvoiceSetting, Partner, PartnerGroup } from '@/types/models'
import { useToast } from '@/stores/toast'
import { Modal } from '@/components/Modal'
import { SearchBox } from '@/components/SearchBox'
import { ShowMoreRow } from '@/components/ShowMoreRow'
import { useRenderLimit } from '@/lib/useListPerf'
import { Field, inputCls, selectCls } from '@/components/Field'
import { cn } from '@/lib/utils'
import { useCrudMutations } from '@/lib/useCrudMutations'
import { useListNavigation } from '@/lib/useListNavigation'
import { useFormFieldNavigation } from '@/lib/useFormFieldNavigation'
import { useFileDownload } from '@/lib/useFileDownload'

const PARTNER_FORM_FIELDS = [
  'name', 'biz_class', 'vat_type', 'biz_no', 'rep_name',
  'biz_kind', 'biz_item', 'address', 'tel', 'fax',
  'group', 'show_previous_price',
] as const

type BizClass = 'customer' | 'vendor' | 'both'

interface PartnerForm {
  id?: number
  code: string; name: string; biz_class: BizClass
  biz_no: string; rep_name: string
  biz_kind: string; biz_item: string; address: string; tel: string; fax: string
  vat_type: 'vat' | 'none'
  group: number | null
  show_previous_price: boolean
  is_active: boolean
}

const BIZ_LABEL: Record<BizClass, string> = { customer: '매출처', vendor: '매입처', both: '매출+매입' }
const BIZ_CLS: Record<BizClass, string> = {
  customer: 'bg-blue-100 text-blue-800',
  vendor:   'bg-purple-100 text-purple-800',
  both:     'bg-emerald-100 text-emerald-800',
}

const blankForm: PartnerForm = {
  code: '', name: '', biz_class: 'customer',
  biz_no: '', rep_name: '',
  biz_kind: '', biz_item: '', address: '', tel: '', fax: '',
  vat_type: 'none', group: null,
  show_previous_price: false,
  is_active: true,
}


type ActiveFilter = 'active' | 'inactive' | 'all'

export function PartnersTab() {
  const qc = useQueryClient()
  const push = useToast((s) => s.push)

  const [search, setSearch] = useState('')
  const [bizClass, setBizClass] = useState('')
  const [groupFilter, setGroupFilter] = useState<string>('')  // '', 'none', or group id
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('active')
  const { download, downloading } = useFileDownload()
  const [edit, setEdit] = useState<PartnerForm | null>(null)
  const [invoiceSettingFor, setInvoiceSettingFor] = useState<Partner | null>(null)

  const { data: groups = [] } = useQuery({
    queryKey: ['partner-groups', ''],
    queryFn: () => api<PartnerGroup[]>('/api/partner-groups'),
  })

  // 새 결과가 올 때까지 이전 목록 유지(1,500줄 표를 비웠다 다시 그리지 않게)
  const { data: rawRows = [] } = useQuery({
    placeholderData: keepPreviousData,
    queryKey: ['partners', search, bizClass, groupFilter, activeFilter === 'active'],
    queryFn: () => {
      const params = new URLSearchParams()
      if (search) params.set('q', search)
      if (bizClass) params.set('biz_class', bizClass)
      if (groupFilter) params.set('group_id', groupFilter)
      params.set('active', activeFilter === 'active' ? '1' : '0')
      return api<Partner[]>(`/api/partners?${params}`)
    },
  })

  const filtered = useMemo(() => {
    if (activeFilter === 'inactive') return rawRows.filter((r) => !r.is_active)
    return rawRows
  }, [rawRows, activeFilter])
  // 표 그리기는 입력보다 뒤로(그리는 중에도 검색창 타이핑이 바로 반영) — 그린푸드 거래처 1,500여 곳
  const rows = useDeferredValue(filtered)
  // 앞 200줄만 그리고 '더 보기'(다른 매핑 화면과 같음) — 1,500줄을 한 번에 그리면 그동안 입력이 멈춤
  const list = useRenderLimit(rows, `${search}|${bizClass}|${groupFilter}|${activeFilter}`)

  const { formRef, onKeyDown: onFormKeyDown } = useFormFieldNavigation(PARTNER_FORM_FIELDS)

  const { tbodyRef, onKeyDown: onListKeyDown } = useListNavigation<Partner>({
    items: list.shown,
    disabled: !!edit || !!invoiceSettingFor,
    onEnter: (r) => setEdit({
      id: r.id, code: r.code, name: r.name, biz_class: r.biz_class,
      biz_no: r.biz_no, rep_name: r.rep_name,
                      biz_kind: r.biz_kind, biz_item: r.biz_item, address: r.address, tel: r.tel, fax: r.fax,
                      vat_type: r.vat_type,
      group: r.group,
      show_previous_price: r.show_previous_price,
      is_active: r.is_active,
    }),
    onDelete: (r) => {
      if (r.is_active) void confirm(`'${r.name}'을 비활성화 할까요?`, { danger: true }).then((ok) => ok && remove.mutate(r.id))
    },
  })

  const { save, remove } = useCrudMutations<PartnerForm, Partner>({
    apiBase: '/api/partners',
    queryKey: 'partners',
    onSaved: () => setEdit(null),
    deleteToast: '비활성화됨',
  })

  const restore = useMutation({
    mutationFn: (id: number) =>
      api<Partner>(`/api/partners/${id}`, { method: 'PATCH', body: { is_active: true } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partners'] })
      push('복원됨', 'success')
    },
    onError: (e) => push(`복원 실패: ${e.message}`, 'error', 5000),
  })

  const removeMutate = remove.mutate
  const restoreMutate = restore.mutate
  // 줄 JSX 는 목록이 바뀔 때만 다시 만든다(검색창 입력·필터 변경 때 1,500줄 재조정 방지)
  const rowEls = useMemo(() => list.shown.map((r, idx) => (
              <tr key={r.id} data-idx={idx} tabIndex={idx === 0 ? 0 : -1}
                  className={cn(
                    'border-t border-slate-100 focus:bg-blue-50 focus:outline-none',
                    r.is_active ? 'hover:bg-slate-50' : 'bg-slate-50/60 text-slate-400 hover:bg-slate-100/60',
                  )}>
                  <td className={cn('px-3 py-2 font-mono text-xs tabular-nums', r.is_active ? 'text-slate-600' : 'text-slate-400')}>{r.code}</td>
                  <td className={cn('px-3 py-2 font-medium', r.is_active ? 'text-slate-900' : 'text-slate-500 line-through')}>
                    {r.name}
                    {!r.is_active && (
                      <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-semibold no-underline">비활성</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {r.group_name ? (
                      <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{r.group_name}</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={cn('text-xs px-2 py-0.5 rounded-full font-semibold',
                      r.is_active ? BIZ_CLS[r.biz_class] : 'bg-slate-200 text-slate-500')}>
                      {BIZ_LABEL[r.biz_class]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs tabular-nums">{r.biz_no}</td>
                  <td className="px-3 py-2">{r.rep_name}</td>
                  <td className="px-3 py-2 text-center text-xs">
                    {r.vat_type === 'vat' ? (
                      <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">포함</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-500">면세</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setInvoiceSettingFor(r)}
                            title="거래명세서 양식"
                            className="text-slate-500 hover:text-emerald-700 text-xs mr-2 inline-flex items-center gap-0.5">
                      <FileCog className="w-3.5 h-3.5"/>명세서
                    </button>
                    <button onClick={() => setEdit({
                      id: r.id, code: r.code, name: r.name, biz_class: r.biz_class,
                      biz_no: r.biz_no, rep_name: r.rep_name,
                      biz_kind: r.biz_kind, biz_item: r.biz_item, address: r.address, tel: r.tel, fax: r.fax,
                      vat_type: r.vat_type,
                      group: r.group,
                      show_previous_price: r.show_previous_price,
                      is_active: r.is_active,
                    })} className="text-blue-600 hover:text-blue-800 text-xs mr-2">수정</button>
                    {r.is_active ? (
                      <button onClick={() => { void confirm(`'${r.name}'을 비활성화 할까요?`, { danger: true }).then((ok) => ok && removeMutate(r.id)) }}
                              className="text-rose-500 hover:text-rose-700 text-xs">삭제</button>
                    ) : (
                      <button onClick={() => restoreMutate(r.id)}
                              className="text-emerald-600 hover:text-emerald-800 text-xs inline-flex items-center gap-0.5">
                        <RotateCcw className="w-3 h-3" /> 복원
                      </button>
                    )}
                  </td>
              </tr>
            )), [list.shown, removeMutate, restoreMutate])

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-end mb-3">
        <button onClick={() => setEdit({ ...blankForm })}
                data-role="add-btn"
                title="Alt+N"
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> 거래처 추가
        </button>
      </div>

      <div className="sticky top-0 z-20 -mx-6 px-6 py-2 bg-slate-50/95 backdrop-blur border-b border-slate-200/60 mb-3">
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap shadow-sm">
        <SearchBox value={search} onChange={setSearch}
                   placeholder="코드, 이름, 사업자번호, 대표자 검색" className="flex-1 min-w-[200px]" />
        <select value={bizClass} onChange={(e) => setBizClass(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100">
          <option value="">전체 구분</option>
          <option value="customer">매출처</option>
          <option value="vendor">매입처</option>
          <option value="both">매출+매입</option>
        </select>
        <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100">
          <option value="">전체 상위</option>
          <option value="none">상위 없음</option>
          {groups.map((g) => (
            <option key={g.id} value={String(g.id)}>{g.name} ({g.member_count})</option>
          ))}
        </select>
        <div className="inline-flex rounded-lg overflow-hidden border border-slate-300">
          {(['active', 'inactive', 'all'] as const).map((f) => (
            <button key={f} onClick={() => setActiveFilter(f)}
                    className={cn('px-3 py-2 text-xs font-semibold transition',
                      activeFilter === f
                        ? f === 'inactive' ? 'bg-slate-600 text-white' : 'bg-blue-600 text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-50')}>
              {f === 'active' ? '활성' : f === 'inactive' ? '비활성' : '전체'}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-400 tabular-nums">{rows.length}건</span>
        <button onClick={() => {
                  // 화면 필터 그대로 엑셀로(검색·구분·상위·활성)
                  const params = new URLSearchParams()
                  if (search) params.set('q', search)
                  if (bizClass) params.set('biz_class', bizClass)
                  if (groupFilter) params.set('group_id', groupFilter)
                  params.set('active', activeFilter === 'active' ? '1' : activeFilter === 'inactive' ? 'inactive' : '0')
                  void download(`/api/partners/export?${params}`, '거래처.xlsx')
                }}
                disabled={downloading || !rows.length}
                className="ml-auto flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
          엑셀 다운로드
        </button>
      </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
            <tr>
              <th className="px-3 py-2.5 text-left font-medium w-24">코드</th>
              <th className="px-3 py-2.5 text-left font-medium">거래처명</th>
              <th className="px-3 py-2.5 text-left font-medium w-40">상위</th>
              <th className="px-3 py-2.5 text-left font-medium w-24">구분</th>
              <th className="px-3 py-2.5 text-left font-medium w-32">사업자번호</th>
              <th className="px-3 py-2.5 text-left font-medium w-24">대표자</th>
              <th className="px-3 py-2.5 text-center font-medium w-20">VAT</th>
              <th className="px-3 py-2.5 text-right font-medium w-40"></th>
            </tr>
          </thead>
          <tbody ref={tbodyRef} onKeyDown={onListKeyDown}>
            {rowEls}
            <ShowMoreRow hidden={list.hidden} colSpan={8} onMore={list.more} onAll={list.all}/>
            {!rows.length && (
              <tr><td colSpan={8} className="text-center text-slate-400 py-10 text-sm">
                {activeFilter === 'inactive' ? '비활성 거래처가 없습니다.' : '거래처가 없습니다.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? '거래처 수정' : '거래처 추가'} size="xl">
        {edit && (
          <form ref={formRef} onKeyDown={onFormKeyDown}
                onSubmit={(e) => { e.preventDefault(); save.mutate(edit) }}
                className="grid grid-cols-2 gap-3">
            <Field label="거래처코드">
              {edit.id ? (
                <input value={edit.code} readOnly tabIndex={-1}
                       className={inputCls + ' tabular-nums bg-slate-50 text-slate-500 cursor-not-allowed'}/>
              ) : (
                <input value="저장 시 자동 부여" readOnly tabIndex={-1}
                       className={inputCls + ' bg-slate-50 text-slate-400 italic cursor-not-allowed'}/>
              )}
            </Field>
            <Field label="거래처명 *"><input data-form-field="name" autoFocus value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} required className={inputCls}/></Field>
            <Field label="구분">
              <select data-form-field="biz_class" value={edit.biz_class} onChange={(e) => setEdit({ ...edit, biz_class: e.target.value as BizClass })} className={selectCls}>
                <option value="customer">매출처</option>
                <option value="vendor">매입처</option>
                <option value="both">매출+매입</option>
              </select>
            </Field>
            <Field label="VAT 기본값">
              <select data-form-field="vat_type" value={edit.vat_type} onChange={(e) => setEdit({ ...edit, vat_type: e.target.value as 'vat' | 'none' })} className={selectCls}>
                <option value="none">VAT 없음</option>
                <option value="vat">VAT 포함</option>
              </select>
            </Field>
            <Field label="사업자번호"><input data-form-field="biz_no" value={edit.biz_no} onChange={(e) => setEdit({ ...edit, biz_no: e.target.value })} className={inputCls + ' tabular-nums'}/></Field>
            <Field label="대표자"><input data-form-field="rep_name" value={edit.rep_name} onChange={(e) => setEdit({ ...edit, rep_name: e.target.value })} className={inputCls}/></Field>
            <Field label="업태"><input data-form-field="biz_kind" value={edit.biz_kind} onChange={(e) => setEdit({ ...edit, biz_kind: e.target.value })} className={inputCls} placeholder="예: 서비스업"/></Field>
            <Field label="종목"><input data-form-field="biz_item" value={edit.biz_item} onChange={(e) => setEdit({ ...edit, biz_item: e.target.value })} className={inputCls} placeholder="예: 음식점업"/></Field>
            <Field label="주소" full><input data-form-field="address" value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} className={inputCls} placeholder="사업장 주소 (거래명세서에 출력)"/></Field>
            <Field label="전화번호"><input data-form-field="tel" value={edit.tel} onChange={(e) => setEdit({ ...edit, tel: e.target.value })} className={inputCls + ' tabular-nums'}/></Field>
            <Field label="팩스번호"><input data-form-field="fax" value={edit.fax} onChange={(e) => setEdit({ ...edit, fax: e.target.value })} className={inputCls + ' tabular-nums'}/></Field>
            <Field label="상위 거래처" full>
              <select data-form-field="group" value={edit.group ?? ''}
                      onChange={(e) => setEdit({ ...edit, group: e.target.value === '' ? null : Number(e.target.value) })}
                      className={selectCls}>
                <option value="">(없음)</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </Field>
            <Field label="직전단가 표시" full>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  data-form-field="show_previous_price"
                  type="checkbox"
                  checked={edit.show_previous_price}
                  onChange={(e) => setEdit({ ...edit, show_previous_price: e.target.checked })}
                  className="accent-emerald-600 w-4 h-4"
                />
                <span>주문 라인 편집 시 단가 옆에 직전 청구 단가 표시</span>
              </label>
              <div className="text-[11px] text-slate-400 mt-0.5">
                컨벤션 등 매일 시가로 단가를 재입력하는 매출처의 오타 방지용.
              </div>
            </Field>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEdit(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit" className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg">저장</button>
            </div>
          </form>
        )}
      </Modal>

      <InvoiceSettingsModal
        partner={invoiceSettingFor}
        onClose={() => setInvoiceSettingFor(null)}
      />
    </div>
  )
}

// ─── InvoiceSettingsModal ─────────────────────────────────────────────
// 파트너별 거래명세서 양식 (16개 옵션) 편집.
function InvoiceSettingsModal({
  partner, onClose,
}: { partner: Partner | null; onClose: () => void }) {
  const push = useToast((s) => s.push)
  const qc = useQueryClient()

  const { data: setting } = useQuery({
    queryKey: ['invoice-setting', partner?.id],
    queryFn: () => api<InvoiceSetting>(`/api/invoice/setting?partner=${partner!.id}`),
    enabled: !!partner,
  })
  const [local, setLocal] = useState<InvoiceSetting | null>(null)
  useEffect(() => { if (setting) setLocal(setting) }, [setting])

  const save = useMutation({
    mutationFn: (s: InvoiceSetting) =>
      api<InvoiceSetting>(`/api/invoice/setting?partner=${partner!.id}`, {
        method: 'PUT', body: s,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice-setting', partner?.id] })
      push('명세서 설정 저장됨', 'success'); onClose()
    },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error', 5000),
  })

  function toggle<K extends keyof InvoiceSetting>(key: K) {
    if (!local) return
    setLocal({ ...local, [key]: !local[key] } as InvoiceSetting)
  }
  function setField<K extends keyof InvoiceSetting>(key: K, v: InvoiceSetting[K]) {
    if (!local) return
    setLocal({ ...local, [key]: v })
  }
  const CheckRow = ({ label, k }: { label: string; k: keyof InvoiceSetting }) => (
    <label className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg text-sm cursor-pointer hover:bg-slate-50">
      <input type="checkbox" checked={local ? (local[k] as boolean) : false} onChange={() => toggle(k)}/>
      {label}
    </label>
  )

  return (
    <Modal open={!!partner} onClose={onClose}
           title={partner ? `거래명세서 양식 - ${partner.code} ${partner.name}` : '거래명세서 양식'}
           size="xl">
      {local ? (
        <>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <CheckRow label="1. 공급자란 인쇄" k="show_supplier_seal" />
            <CheckRow label="2. 배송지주소" k="show_delivery_addr" />
            <CheckRow label="3. 수량계 인쇄" k="show_qty_total" />
            <CheckRow label="4. 부가가치세 표시" k="show_vat" />
            <CheckRow label="5. 소수점 표시" k="show_decimal" />
            <CheckRow label="6. 수량 표시" k="show_qty" />
            <CheckRow label="7. 단가 표시" k="show_unit_price" />
            <CheckRow label="8. 금액 표시" k="show_amount" />
            <CheckRow label="9. 합계금액 표시" k="show_total_amount" />
            <CheckRow label="10. 채권 표시" k="show_receivable" />
            <div className="col-span-2 p-2 border border-slate-200 rounded-lg">
              <label className="text-xs font-semibold text-slate-600 mb-1 block">11. 담당자 인쇄</label>
              <select value={local.representative_type}
                      onChange={(e) => setField('representative_type', e.target.value)}
                      className="w-full px-2 py-1 border border-slate-300 rounded text-sm bg-white">
                <option value="supplier">1. 공급자</option>
                <option value="partner_b">2. 거래처-기초</option>
                <option value="partner_a">3. 거래처-추가</option>
                <option value="delivery">4. 배송지</option>
              </select>
            </div>
            <CheckRow label="12. 품목비고 표시" k="show_item_memo" />
            <div className="col-span-2 p-2 border border-slate-200 rounded-lg">
              <label className="text-xs font-semibold text-slate-600 mb-1 block">13. 거래명세서비고 표시</label>
              <select value={local.invoice_memo_field}
                      onChange={(e) => setField('invoice_memo_field', e.target.value)}
                      className="w-full px-2 py-1 border border-slate-300 rounded text-sm bg-white">
                <option value="memo1">1. 비고1</option>
                <option value="memo2">2. 비고2</option>
                <option value="memo3">3. 비고3</option>
                <option value="all">4. 전체</option>
              </select>
            </div>
            <CheckRow label="14. 규격 표시" k="show_spec" />
            <CheckRow label="15. 순백지1,2 세액 표시" k="show_tax_amount" />
            <CheckRow label="16. 당일거래총액 표시" k="show_today_total" />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <button onClick={onClose}
                    className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
              취소
            </button>
            <button onClick={() => save.mutate(local)}
                    disabled={save.isPending}
                    className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg disabled:opacity-50">
              {save.isPending ? '저장 중...' : '저장'}
            </button>
          </div>
        </>
      ) : (
        <div className="py-8 text-center text-slate-400 text-sm">불러오는 중...</div>
      )}
    </Modal>
  )
}
